import { useMemo, type ReactNode } from "react";
import { TableProvider } from "../../Table/Internal/TableContext";
import { ControlBarProvider } from "../../PageLayout/Internal/ControlBarContext";
import { BannerProvider } from "../../Display/Internal/BannerContext";
import { MenuProvider } from "../../Menu/Internal/MenuContext";
import { DialogHeaderProvider } from "../../Dialog/Internal/DialogHeaderContext";

const noop = () => {};

/**
 * Resets every layout context that adapts how a `Button` renders (Table,
 * ControlBar, Banner, Menu, DialogHeader) back to its neutral default. Mount
 * this at the top of any PORTALED surface — popover content,
 * dialog content, dropdown menus, tooltips.
 *
 * Why: a React Portal moves the DOM to `document.body` but PRESERVES the
 * React context tree. A popover opened from inside a `<Table>` therefore
 * still reads `isInTable === true`, so its Buttons collapse to the icon-only
 * dense-row treatment ("Add to List" in CompanyCell's hover card rendering
 * as a bare icon was the first sighting). A floating surface is its own
 * layout root — none of the layout adaptations of the surface it was opened
 * FROM should apply inside it.
 *
 * Surfaces that want one of these adaptations back simply provide it again
 * inside (e.g. Menu re-provides MenuContext for its items; a Dialog header
 * re-provides DialogHeaderContext for its actions slot) — the reset only
 * severs inheritance across the portal boundary, it doesn't disable the
 * mechanisms.
 */
export function PortalContextReset({ children }: { children: ReactNode }) {
  // MenuContext's value is an object; memo it so the reset doesn't hand a
  // fresh identity to consumers on every render.
  const menuReset = useMemo(
    () => ({ isInMenu: false, closeMenu: noop, showDialog: noop }),
    [],
  );
  return (
    <TableProvider value={false}>
      <ControlBarProvider value={false}>
        <BannerProvider value={false}>
          <MenuProvider value={menuReset}>
            <DialogHeaderProvider value={false}>
              {children}
            </DialogHeaderProvider>
          </MenuProvider>
        </BannerProvider>
      </ControlBarProvider>
    </TableProvider>
  );
}
